/**
 * Endpoint Registry - Centralized API endpoint definitions
 * @description Replaces manual URL concatenation (params.apiUrl + "...") with typed endpoint references
 * @see Phase 2: Frontend Endpoint Registry
 */

// ============================================================================
// Standard API Endpoints - Route through /api proxy (vue.config.js)
// ============================================================================
export const API_ENDPOINTS = {
  // Auth
  "auth.check": "auth/auth_token_check",
  "auth.login": "auth/login",
  "auth.logout": "auth/logout",

  // Users
  "users.fields": "users/fields",
  "users.items": "users/items",
  "users.create": "users/create",
  "users.update": (params) => `/users/edit/${params?.id}`,
  "users.delete": "users/delete",
  "users.loginAs": "users/login_as_user",

  // Pages
  "pages.list": "pages/list",
  "pages.fields": "pages/fields",
  "pages.create": "pages/create",
  "pages.update": "pages/update",
  "pages.delete": "pages/delete",

  // Modules
  "modules.list": "modules/list",
  "modules.fields": "modules/fields",
  "modules.create": "modules/create",
  "modules.update": "modules/update",
  "modules.delete": "modules/delete",
  "modules.items": "modules/items",
  "modules.item": "modules/item",
  "modules.saveItem": "modules/save_item",
  "modules.deleteItem": "modules/delete_item",

  // Dictionaries
  "dict.orderStatuses": "dict/order_statuses",
  "dict.deliveryMethods": "dict/delivery_methods",
  "dict.paymentMethods": "dict/payment_methods",
  "dict.countries": "dict/countries",
  "dict.cities": "dict/cities",
  "dict.regions": "dict/regions",

  // Common
  "common.dicts": "common/dicts",
  "common.modulesMenu": "common/modules_menu",

  // Custom box endpoints (standard API endpoints; baseURL '/api' is applied by api client)
  "custom.filledBoxes": (module) => `custom/filled_boxes/${module}`,
  "custom.getBoxPublishDate": "custom/get_box_publish_date",
  "custom.validate1cData": "custom/validate_1c_data",
  "custom.cleanBox": "custom/clean_box",
  "custom.publishBox": "custom/publish_box",
  "custom.sendTo1c": "custom/send_to_1c",
  "custom.boxCalcPrices": (params) =>
    `custom/box_calc_prices/${params.module}/${params.box}`,
  "custom.boxCalcPricesSave": "custom/box_calc_prices_save",
  "custom.boxPublishDate": "custom/box_publish_date",
  "custom.boxExport": (params) =>
    `custom/box_export/${params.module}/${params.box}`,

  // Orders endpoints
  "orders.maxId": "get_orders_max_id",
  "orders.list": "get_orders",
  "orders.checkNew": "check_new_orders",
  "orders.utmList": "custom/get_orders_utm_list",

  // Services (1C integration endpoints)
  "services.sales": "services/sales",
  "services.salesLoad": "services/sales/load",
  "services.salesSubmit": "services/sales/submit",
  "services.get1CDocuments": "services/1c-documents",
  "services.get1CTemplates": "services/1c-templates",
  "services.get1CSources": "services/1c-sources",
  "services.revaluationProcess": "services/revaluation/process",
  "services.tagSync": "services/tag-sync",
  "services.tagExport": "services/tag-export",

  // Marketplace endpoints
  "marketplace.status": "marketplace/status",
  "marketplace.ozonPresence": "marketplace/ozon/presence",
  "marketplace.ozonPublish": "marketplace/ozon/publish",
  "marketplace.ozonPhoto": "marketplace/ozon/photo",
  "marketplace.wbCreate": "marketplace/wb/create",
  "marketplace.wbUpdate": "marketplace/wb/update",
  "marketplace.wbPhoto": "marketplace/wb/photo",
  "marketplace.wbPresence": "marketplace/wb/presence",
}

// ============================================================================
// Legacy API Endpoints - Route through /edit/new_api proxy (vue.config.js)
// These endpoints require the legacy /edit/new_api/ path prefix
// NOTE: Custom endpoints have been migrated to /api proxy (see API_ENDPOINTS)
// Remaining legacy endpoints (pages, users, export) await backend route updates
// ============================================================================
export const LEGACY_API_ENDPOINTS = {
  // Pages (legacy)
  "legacy.pages.list": "/edit/new_api/pages/list",
  "legacy.pages.fields": "/edit/new_api/pages/fields",
  "legacy.pages.create": "/edit/new_api/pages/create",
  "legacy.pages.update": "/edit/new_api/pages/update",
  "legacy.pages.delete": "/edit/new_api/pages/delete",

  // Users (legacy)
  "legacy.users.fields": "/edit/new_api/users/fields",
  "legacy.users.groups": "/edit/new_api/users/groups",
  "legacy.users.info": (params) => `/edit/new_api/users/info/${params.id}`,
  "legacy.users.items": "/edit/new_api/users/items",
  "legacy.users.create": "/edit/new_api/users/create",
  "legacy.users.edit": (params) => `/edit/new_api/users/edit/${params.id}`,
  "legacy.users.new": "/edit/new_api/users/new",
  "legacy.users.update": "/edit/new_api/users/update",
  "legacy.users.delete": "/edit/new_api/users/delete",
  "legacy.users.loginAs": "/edit/new_api/loginasuser",

  // Export endpoints
  "legacy.export.box": (boxId) => `/edit/new_api/export_data/box/${boxId}`,
  "legacy.export.boxWithModule": (params) =>
    `/edit/new_api/export_data/box/${params.boxId}/${params.module}`,
  "legacy.export.boxes": "/edit/new_api/export_data/boxes",
  "api.export.box": (boxId) => `/api/edit/new_api/export_data/box/${boxId}`,
  "api.export.boxWithModule": (params) =>
    `/api/edit/new_api/export_data/box/${params.boxId}/${params.module}`,

  // Box upload endpoints
  "legacy.fillBoxFromExcel": "/edit/new_api/fill_box_from_excel",
}

// ============================================================================
// Report Endpoints - Route through /edit/newreports proxy (vue.config.js)
// ============================================================================
export const REPORT_ENDPOINTS = {
  "reports.users": "/edit/newreports/users",
  "reports.orders": "/edit/newreports/orders",
  "reports.finance": "/edit/newreports/finance",
  "reports.products": "/edit/newreports/products",
}

// ============================================================================
// Helper: Get endpoint URL by name
// ============================================================================

/**
 * Get endpoint URL by name with optional path parameters
 * @param {string} name - Endpoint name (e.g., "users.items", "reports.users")
 * @param {Object} params - Optional path parameters for dynamic endpoints
 * @returns {string} Full endpoint URL
 * @throws {Error} If endpoint not found
 *
 * @example
 * buildEndpoint("users.items") // => "users/items"
 * buildEndpoint("legacy.export.box", { boxId: 123 }) // => "/edit/new_api/export_data/box/123"
 * buildEndpoint("reports.users") // => "/edit/newreports/users"
 */
export function buildEndpoint(name, params = {}) {
  // Check API_ENDPOINTS first
  if (API_ENDPOINTS[name]) {
    const endpoint = API_ENDPOINTS[name]
    // Handle dynamic functions
    if (typeof endpoint === "function") {
      return endpoint(params)
    }
    return endpoint
  }

  // Check LEGACY_API_ENDPOINTS
  if (LEGACY_API_ENDPOINTS[name]) {
    const endpoint = LEGACY_API_ENDPOINTS[name]
    // Handle dynamic functions
    if (typeof endpoint === "function") {
      return endpoint(params)
    }
    return endpoint
  }

  // Check REPORT_ENDPOINTS
  if (REPORT_ENDPOINTS[name]) {
    return REPORT_ENDPOINTS[name]
  }

  throw new Error(`[endpoints] Unknown endpoint: "${name}"`)
}

/**
 * Build full backend URL for legacy/report endpoints that bypass the /api proxy.
 * Use this when you need a complete URL (e.g., for window.location.href navigation).
 * @param {string} name - Endpoint name
 * @param {Object} params - Optional path parameters
 * @returns {string} Full URL with origin (e.g., "http://localhost:28081/edit/new_api/loginasuser")
 */
export function buildBackendUrl(name, params = {}) {
  const path = buildEndpoint(name, params)
  if (typeof window === "undefined") return path
  const origin = window.location.origin || ""
  return `${origin}${path}`
}

/**
 * Get all available endpoint names
 * @returns {string[]} Array of all endpoint names
 */
export function getAllEndpointNames() {
  return [
    ...Object.keys(API_ENDPOINTS),
    ...Object.keys(LEGACY_API_ENDPOINTS),
    ...Object.keys(REPORT_ENDPOINTS),
  ]
}

// ============================================================================
// Constants for endpoint types
// ============================================================================
export const ENDPOINT_TYPES = {
  STANDARD: "STANDARD",
  LEGACY: "LEGACY",
  REPORT: "REPORT",
}

/**
 * Get endpoint type
 * @param {string} name - Endpoint name
 * @returns {string} Endpoint type
 */
export function getEndpointType(name) {
  if (name in API_ENDPOINTS) return ENDPOINT_TYPES.STANDARD
  if (name in LEGACY_API_ENDPOINTS) return ENDPOINT_TYPES.LEGACY
  if (name in REPORT_ENDPOINTS) return ENDPOINT_TYPES.REPORT
  return null
}
